var _th, _Glyphicon, _Glyphicon2;
import PropTypes from 'prop-types';
import React, { useRef, useEffect, useCallback, useState } from 'react';
import _isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';
import { smoothScrollByPosition } from '@atc/dom-fns';
import Button from '../Button/index.js';
import Glyphicon from '../Glyphicon/index.js';
import Table from '../Table/index.js';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const renderTableHeader = _ref => {
  let {
    tableHeadings,
    columnHeadersColor,
    rowHeaderData
  } = _ref;
  return /*#__PURE__*/_jsx("thead", {
    children: /*#__PURE__*/_jsxs("tr", {
      children: [!_isEmpty(rowHeaderData) && (_th || (_th = /*#__PURE__*/_jsx("th", {}))), tableHeadings.map((header, index) => /*#__PURE__*/_jsx("th", {
        style: {
          backgroundColor: columnHeadersColor
        },
        children: header
      }, `th-${index}`))]
    })
  });
};
const renderTableBody = _ref2 => {
  let {
    tableRows,
    rowHeadersColor,
    cellIndexWidth,
    rowHeaderData
  } = _ref2;
  return /*#__PURE__*/_jsx("tbody", {
    children: tableRows.map((row, rowIndex) => /*#__PURE__*/_jsxs("tr", {
      children: [!_isEmpty(rowHeaderData) && /*#__PURE__*/_jsx("td", {
        className: "text-bold",
        style: {
          width: cellIndexWidth
        },
        children: rowHeaderData[rowIndex]
      }), row.map((cell, cellIndex) => {
        if (cellIndex === 0) {
          const width = cellIndexWidth?.index === 0 ? cellIndexWidth?.value : cellIndexWidth;
          return /*#__PURE__*/_jsx("td", {
            style: {
              backgroundColor: rowHeadersColor,
              width
            },
            children: cell
          }, `td-${cellIndex}`);
        }
        if (cellIndexWidth?.index !== 0 && cellIndex === cellIndexWidth?.index) {
          return /*#__PURE__*/_jsx("td", {
            style: {
              width: cellIndexWidth.value
            },
            children: cell
          }, `td-${cellIndex}`);
        }
        return /*#__PURE__*/_jsx("td", {
          style: {
            width: cellIndexWidth
          },
          children: cell
        }, `td-${cellIndex}`);
      })]
    }, `tr-${rowIndex}`))
  });
};
const SuperTable = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  let {
    children,
    tableHeadings,
    tableRows,
    rowHeaderData,
    cellIndexWidth,
    paddingCellWidth,
    columnHeadersColor,
    rowHeadersColor,
    navMarginTop,
    navMarginLeft,
    navMarginRight,
    className,
    inModal = true,
    hideNavButtons = false,
    ...rest
  } = _ref3;
  const [tableContainerRef, setTableContainerRef] = useState(useRef());
  const sliderIndex = useRef(0);
  const prevSliderIndex = useRef(0);
  useEffect(() => {
    if (ref?.current) setTableContainerRef(ref);
  }, [ref]);
  const getPrevElement = list => {
    // TODO: make this index a variable after making the first columnd optionally fixed
    const sibling = list ? list[0]?.previousElementSibling : null;
    const secondSibling = sibling?.previousElementSibling;
    if (secondSibling instanceof HTMLElement) {
      return secondSibling;
    }
    return null;
  };
  const getNextElement = list => {
    const sibling = list[list.length - 1].nextElementSibling;
    const secondSibling = sibling?.nextElementSibling;
    if (secondSibling instanceof HTMLElement) {
      return secondSibling;
    }
    return null;
  };
  const updateIndex = direction => {
    if (direction === 'left') {
      prevSliderIndex.current = sliderIndex.current;
      sliderIndex.current -= 1;
    }
    if (direction === 'right') {
      prevSliderIndex.current = sliderIndex.current;
      sliderIndex.current += 1;
    }
  };
  const usePosition = outerRef => {
    const [prevElement, setPrevElement] = useState(null);
    const [nextElement, setNextElement] = useState(null);
    useEffect(() => {
      const element = outerRef.current;
      if (!element) {
        return () => {};
      }

      // Add defensive checks for DOM structure
      const tableContainer = element.children[1];
      if (!tableContainer) {
        return () => {};
      }
      const tableElement = tableContainer.firstChild;
      if (!tableElement) {
        return () => {};
      }
      const tableHead = tableElement.firstChild;
      if (!tableHead || !tableHead.cells) {
        return () => {};
      }
      const tableTheadCells = tableHead.cells;
      const update = () => {
        const rect = element.getBoundingClientRect();
        const cells = Array.from(tableTheadCells);
        const firstChildRectRight = cells[0].getBoundingClientRect().right;
        const visibleElements = cells.filter((child, index) => {
          const childRect = child.getBoundingClientRect();
          return index > 0 && rect.left <= childRect.left && rect.right >= childRect.right && firstChildRectRight <= childRect.right;
        });
        if (visibleElements.length > 0) {
          setPrevElement(getPrevElement(visibleElements));
          setNextElement(getNextElement(visibleElements));
        }
      };
      update();
      let scrollObserver;
      if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
        scrollObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            update();
          });
        });
        const tableCell = Array.from(tableTheadCells);
        tableCell.forEach(el => {
          scrollObserver.observe(el);
        });
      }
      return () => {
        if (scrollObserver) {
          scrollObserver.disconnect();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outerRef, children, tableHeadings]);
    const scrollToElement = useCallback((element, direction) => {
      if (direction) updateIndex(direction);
      const currentNode = ref.current;
      if (!currentNode || !element) return;
      const newScrollPosition = element.offsetLeft + element.getBoundingClientRect().width / 2 - currentNode.getBoundingClientRect().width / 2;
      smoothScrollByPosition(newScrollPosition, {
        direction: 'left',
        container: currentNode,
        duration: 1000
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [outerRef]);
    const scrollRight = useCallback(() => scrollToElement(nextElement, 'right'), [scrollToElement, nextElement]);
    const scrollLeft = useCallback(() => scrollToElement(prevElement, 'left'), [scrollToElement, prevElement]);
    return {
      hasItemsOnLeft: prevElement !== null,
      hasItemsOnRight: nextElement !== null,
      scrollRight,
      scrollLeft
    };
  };
  const {
    hasItemsOnLeft,
    hasItemsOnRight,
    scrollRight,
    scrollLeft
  } = usePosition(tableContainerRef);
  const superTableNavClass = clsx('super-table-nav', {
    absolute: !inModal
  });
  const nextButtonMarginLeft = !inModal ? `calc(100% - ${navMarginLeft}px - 40px)` : 0; // full width - nav margin - nav button width

  const superTableWithNavigation = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      className: superTableNavClass,
      style: {
        marginLeft: navMarginLeft,
        marginRight: navMarginRight,
        top: navMarginTop
      },
      children: [/*#__PURE__*/_jsx(Button, {
        type: "button",
        className: clsx('previous padding-0', {
          'hidden-xs': !hideNavButtons,
          hidden: hideNavButtons
        }),
        onClick: scrollLeft,
        "aria-label": "Previous",
        disabled: !hasItemsOnLeft,
        children: _Glyphicon || (_Glyphicon = /*#__PURE__*/_jsx(Glyphicon, {
          className: "text-center text-size-700",
          glyph: "menu-left"
        }))
      }), /*#__PURE__*/_jsx(Button, {
        type: "button",
        className: clsx('next padding-0', {
          'hidden-xs': !hideNavButtons,
          hidden: hideNavButtons
        }),
        onClick: scrollRight,
        "aria-label": "Next",
        disabled: !hasItemsOnRight,
        style: {
          marginLeft: nextButtonMarginLeft
        },
        children: _Glyphicon2 || (_Glyphicon2 = /*#__PURE__*/_jsx(Glyphicon, {
          className: "text-center text-size-700",
          glyph: "menu-right"
        }))
      })]
    }), /*#__PURE__*/_jsxs(Table, {
      className: clsx('super-table', className),
      ...rest,
      children: [children, renderTableHeader({
        tableHeadings,
        columnHeadersColor,
        rowHeaderData
      }), renderTableBody({
        tableRows,
        rowHeadersColor,
        cellIndexWidth,
        rowHeaderData
      })]
    })]
  });
  const renderSuperTable = () => {
    const contentWithWrapper = /*#__PURE__*/_jsx("div", {
      ref: tableContainerRef,
      children: superTableWithNavigation
    });
    return ref?.current ? superTableWithNavigation : contentWithWrapper;
  };
  return renderSuperTable();
});
export default SuperTable;